// src/components/WorkflowTemplateEditor.js
import React, { useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, CheckCircle } from "lucide-react";
import { useApi } from "../hooks/useApi";
import MultiUserSelect from "./MultiUserSelect";

/**
 * Canonical remediation statuses a workflow step may map to. "Completed" is deliberately
 * absent: it is engine-owned — the remediation becomes Completed only when the final step
 * is completed (PLAN.md decision 07b), so the editor shows it as a fixed final row instead.
 */
export const STEP_STATUS_OPTIONS = ["Not Started", "Planned", "In Progress", "Under Review", "Deferred"];

const newStep = () => ({
    stepKey: null,
    name: "",
    instructions: "",
    canonicalStatus: "In Progress",
    assigneeUserIds: [],
    dueOffsetDays: null,
    requireComment: false,
});

const toFormState = (template) => ({
    name: template?.name || "",
    description: template?.description || "",
    isDefault: !!template?.isDefault,
    steps: (template?.steps || [])
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((s) => ({
            stepKey: s.stepKey || null,
            name: s.name || "",
            instructions: s.instructions || "",
            canonicalStatus: s.canonicalStatus || "In Progress",
            // Legacy steps carry only assigneeUserId: show it as a one-element list.
            assigneeUserIds: (s.assigneeUserIds ?? (s.assigneeUserId != null ? [s.assigneeUserId] : [])).map(String),
            dueOffsetDays: s.dueOffsetDays ?? null,
            requireComment: !!s.requireComment,
        })),
});

/**
 * Client-side mirror of the server rules (RemediationWorkflowTemplateService.ValidateSteps as
 * amended by 07b). Server messages still win — a 400 body is shown verbatim on save.
 */
export const validateTemplate = (form) => {
    const errors = [];
    if (!form.name.trim()) errors.push("Template name is required.");
    if (form.steps.length === 0) errors.push("A template needs at least one step.");
    form.steps.forEach((s, i) => {
        if (!s.name.trim()) errors.push(`Step ${i + 1} needs a name.`);
        if (!STEP_STATUS_OPTIONS.includes(s.canonicalStatus))
            errors.push(`Step ${i + 1} has an invalid status.`);
        if (s.canonicalStatus === "Completed")
            errors.push(`Step ${i + 1} cannot map to Completed — that happens automatically when the last step completes.`);
        if (s.dueOffsetDays !== null && (!Number.isInteger(s.dueOffsetDays) || s.dueOffsetDays < 0))
            errors.push(`Step ${i + 1} due offset must be a whole number of days (0 or more).`);
    });
    return errors;
};

const toUpsertBody = (form) => ({
    name: form.name.trim(),
    description: form.description.trim(),
    isDefault: form.isDefault,
    steps: form.steps.map((s, i) => ({
        ...(s.stepKey ? { stepKey: s.stepKey } : {}),
        order: i + 1,
        name: s.name.trim(),
        instructions: s.instructions.trim(),
        canonicalStatus: s.canonicalStatus,
        assigneeUserIds: s.assigneeUserIds.map((id) => parseInt(id, 10)),
        assigneeUserId: s.assigneeUserIds.length > 0 ? parseInt(s.assigneeUserIds[0], 10) : null,
        dueOffsetDays: s.dueOffsetDays,
        requireComment: s.requireComment,
    })),
});

const inputCls = "w-full px-3 py-2 border border-border rounded-md text-sm bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-indigo-500";
const labelCls = "block text-xs font-medium text-content-secondary mb-1";
const iconBtnCls = "p-1.5 rounded-md border border-border text-content-secondary hover:text-content-primary hover:bg-theme-hover disabled:opacity-40 disabled:cursor-not-allowed";

/**
 * Inline create/edit panel for a remediation workflow template (Settings → Workflows).
 *
 * Props:
 *   template  – existing template DTO to edit, or null to create a new one
 *   users     – assignable users (plain array from GET /assessments/assignableUsers)
 *   usersLoading – boolean
 *   onSaved(template) – called with the server response after a successful POST/PUT
 *   onCancel()
 */
const WorkflowTemplateEditor = ({ template = null, users = [], usersLoading = false, onSaved, onCancel }) => {
    const apiCall = useApi();
    const [form, setForm] = useState(() => toFormState(template));
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);
    const isEdit = !!template?.id;

    const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    const updateStep = (index, patch) =>
        setForm((f) => ({ ...f, steps: f.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));

    const moveStep = (index, delta) =>
        setForm((f) => {
            const target = index + delta;
            if (target < 0 || target >= f.steps.length) return f;
            const steps = f.steps.slice();
            [steps[index], steps[target]] = [steps[target], steps[index]];
            return { ...f, steps };
        });

    const removeStep = (index) =>
        setForm((f) => ({ ...f, steps: f.steps.filter((_, i) => i !== index) }));

    const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, newStep()] }));

    const handleSave = async () => {
        const clientErrors = validateTemplate(form);
        setErrors(clientErrors);
        if (clientErrors.length > 0) return;

        setSaving(true);
        try {
            const body = toUpsertBody(form);
            const saved = isEdit
                ? await apiCall("PUT", `/remediation-workflows/templates/${template.id}`, body)
                : await apiCall("POST", "/remediation-workflows/templates", body);
            onSaved?.(saved);
        } catch (err) {
            setErrors([err.response?.data?.message || "The template could not be saved. Please try again."]);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-theme-card-solid border border-border rounded-xl p-4 space-y-4" data-testid="workflow-template-editor">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-content-primary">
                    {isEdit ? `Edit template: ${template.name}` : "New template"}
                </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="wf-template-name" className={labelCls}>Name</label>
                    <input
                        id="wf-template-name"
                        type="text"
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        className={inputCls}
                        placeholder="e.g. Standard remediation"
                    />
                </div>
                <div className="flex items-end pb-2">
                    <label className="inline-flex items-center gap-2 text-sm text-content-primary">
                        <input
                            type="checkbox"
                            checked={form.isDefault}
                            onChange={(e) => setField("isDefault", e.target.checked)}
                            className="h-4 w-4 rounded border-border text-indigo-600 focus:ring-indigo-500"
                        />
                        Default template
                    </label>
                </div>
                <div className="md:col-span-2">
                    <label htmlFor="wf-template-description" className={labelCls}>Description</label>
                    <textarea
                        id="wf-template-description"
                        rows={2}
                        value={form.description}
                        onChange={(e) => setField("description", e.target.value)}
                        className={inputCls}
                    />
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-content-primary">Steps</h3>
                    <button
                        type="button"
                        onClick={addStep}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors"
                    >
                        <Plus className="h-3.5 w-3.5" /> Add step
                    </button>
                </div>

                {form.steps.length === 0 && (
                    <div className="py-6 text-center text-sm text-content-secondary border border-dashed border-border rounded-lg">
                        No steps yet. Add at least one step.
                    </div>
                )}

                <ol className="space-y-3">
                    {form.steps.map((step, index) => (
                        <li
                            key={step.stepKey || `new-${index}`}
                            className="border border-border rounded-lg p-3 bg-theme-primary"
                            data-testid={`workflow-step-row-${index}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="flex flex-col items-center gap-1 pt-1">
                                    <span className="text-xs font-semibold text-content-secondary">{index + 1}</span>
                                    <button
                                        type="button"
                                        aria-label={`Move step ${index + 1} up`}
                                        title="Move up"
                                        onClick={() => moveStep(index, -1)}
                                        disabled={index === 0}
                                        className={iconBtnCls}
                                    >
                                        <ArrowUp className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={`Move step ${index + 1} down`}
                                        title="Move down"
                                        onClick={() => moveStep(index, 1)}
                                        disabled={index === form.steps.length - 1}
                                        className={iconBtnCls}
                                    >
                                        <ArrowDown className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={`Remove step ${index + 1}`}
                                        title="Remove step"
                                        onClick={() => removeStep(index)}
                                        className={`${iconBtnCls} hover:text-red-600`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>

                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label htmlFor={`wf-step-name-${index}`} className={labelCls}>Step name</label>
                                        <input
                                            id={`wf-step-name-${index}`}
                                            type="text"
                                            value={step.name}
                                            onChange={(e) => updateStep(index, { name: e.target.value })}
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor={`wf-step-status-${index}`} className={labelCls}>Remediation status while active</label>
                                        <select
                                            id={`wf-step-status-${index}`}
                                            value={step.canonicalStatus}
                                            onChange={(e) => updateStep(index, { canonicalStatus: e.target.value })}
                                            className={inputCls}
                                        >
                                            {STEP_STATUS_OPTIONS.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label htmlFor={`wf-step-instructions-${index}`} className={labelCls}>Instructions</label>
                                        <textarea
                                            id={`wf-step-instructions-${index}`}
                                            rows={2}
                                            value={step.instructions}
                                            onChange={(e) => updateStep(index, { instructions: e.target.value })}
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Assignees (optional)</label>
                                        <MultiUserSelect
                                            value={step.assigneeUserIds}
                                            onChange={(ids) => updateStep(index, { assigneeUserIds: ids })}
                                            users={users}
                                            loading={usersLoading}
                                            placeholder="-- Add an assignee --"
                                        />
                                        {step.assigneeUserIds.length === 0 && (
                                            <p className="text-xs text-content-secondary mt-1">Uses the risk's assignee</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <div>
                                            <label htmlFor={`wf-step-due-${index}`} className={labelCls}>Due (days after activation, optional)</label>
                                            <input
                                                id={`wf-step-due-${index}`}
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={step.dueOffsetDays ?? ""}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    updateStep(index, { dueOffsetDays: raw === "" ? null : Number(raw) });
                                                }}
                                                className={inputCls}
                                            />
                                        </div>
                                        <label className="inline-flex items-center gap-2 text-sm text-content-primary">
                                            <input
                                                type="checkbox"
                                                checked={step.requireComment}
                                                onChange={(e) => updateStep(index, { requireComment: e.target.checked })}
                                                className="h-4 w-4 rounded border-border text-indigo-600 focus:ring-indigo-500"
                                            />
                                            Require a comment to complete this step
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                    {/* Fixed final row: Completed is engine-owned, never a template step. */}
                    <li className="border border-dashed border-border rounded-lg p-3 flex items-center gap-3 text-sm text-content-secondary" data-testid="workflow-step-final">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>
                            <span className="font-medium text-content-primary">Remediation completed</span>
                            {" "}— set automatically when the last step above is completed.
                        </span>
                    </li>
                </ol>
            </div>

            {errors.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" role="alert">
                    <ul className="list-disc list-inside space-y-0.5">
                        {errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={saving}
                    className="px-4 py-2 border border-border rounded-md text-sm text-content-secondary hover:bg-theme-hover disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                    {saving ? "Saving..." : "Save template"}
                </button>
            </div>
        </div>
    );
};

export default WorkflowTemplateEditor;
